import { googleLogin } from "api/auth";
import { ApiError } from "api/client";
import { globalConfig } from "config";
import { useFormikContext } from "formik";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { getPostSignInPath } from "routes/signInRedirect";
import { useSession } from "Session";
import { clearAcquisitionMetadata, readAcquisitionMetadata } from "utilities";

import type { GoogleCredentialResponse, GoogleSignInButtonProps } from "./google-signin-button.types";

import styles from "./GoogleSignInButton.module.css";

const GSI_SCRIPT_SRC = "https://accounts.google.com/gsi/client";
const GSI_SCRIPT_ID = "google-identity-services";

/**
 * Renders the official Google Identity Services button (Google requires its own rendered button for
 * the ID-token flow). On success the returned ID token is exchanged with the API for a session.
 * Errors surface through the surrounding Formik status, so it must live inside a Formik form.
 */
const GoogleSignInButton = ({ acceptedTerms, onConsentRequired }: GoogleSignInButtonProps) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const navigate = useNavigate();
	const location = useLocation();
	const { signIn } = useSession();
	// The register page carries a Turnstile token in its form; the sign-in page has no such field,
	// where undefined is correct — signing in creates nothing, so there is no challenge to pass.
	const { setStatus, values } = useFormikContext<{ turnstileToken?: string }>();
	const { t } = useTranslation("accountsPage");
	const [isButtonRendered, setIsButtonRendered] = useState(false);

	// Held in a ref rather than read from the closure: `onCredential` is what GSI is initialised
	// with, so letting the consent state into its dependency list would tear down and re-render
	// Google's button on every tick of the checkbox.
	const consentRef = useRef({ acceptedTerms, onConsentRequired, turnstileToken: values.turnstileToken });

	useEffect(() => {
		consentRef.current = { acceptedTerms, onConsentRequired, turnstileToken: values.turnstileToken };
	}, [acceptedTerms, onConsentRequired, values.turnstileToken]);

	const onCredential = useCallback(
		async (response: GoogleCredentialResponse): Promise<void> => {
			const consent = consentRef.current;

			// Only pages that supply a handler gate on consent — that is the register page, where the
			// checkbox exists. Stopping here keeps the failure in the form instead of round-tripping
			// to Google and back for a rejection.
			if (consent.onConsentRequired && !consent.acceptedTerms) {
				consent.onConsentRequired();

				return;
			}

			try {
				// This endpoint both signs in and provisions on first use, so attribution goes with
				// every call; the API discards it unless it is actually creating the account. The same
				// is true of the consent flag: ignored for a returning user, required to provision.
				const auth = await googleLogin(
					response.credential,
					readAcquisitionMetadata(),
					consent.acceptedTerms === true,
					consent.turnstileToken
				);
				clearAcquisitionMetadata();
				signIn(auth);
				navigate(getPostSignInPath(location.search), { replace: true });
			} catch (error) {
				setStatus(error instanceof ApiError ? error.message : t("googleError"));
			}
		},
		[location.search, navigate, setStatus, signIn, t]
	);

	useEffect(() => {
		if (!globalConfig.googleClientId) {
			return undefined;
		}

		const renderButton = (): void => {
			if (!containerRef.current || !window.google) {
				return;
			}

			window.google.accounts.id.initialize({ client_id: globalConfig.googleClientId, callback: onCredential });
			window.google.accounts.id.renderButton(containerRef.current, {
				theme: "outline",
				size: "large",
				text: "continue_with",
				width: containerRef.current.offsetWidth
			});
			setIsButtonRendered(true);
		};

		if (window.google) {
			renderButton();

			return undefined;
		}

		// The GSI script is shared between the login and register pages; load it once and re-render on load.
		let script = document.getElementById(GSI_SCRIPT_ID) as HTMLScriptElement | null;

		if (!script) {
			script = document.createElement("script");
			script.id = GSI_SCRIPT_ID;
			script.src = GSI_SCRIPT_SRC;
			script.async = true;
			document.head.appendChild(script);
		}

		script.addEventListener("load", renderButton);

		return () => script?.removeEventListener("load", renderButton);
	}, [onCredential]);

	if (!globalConfig.googleClientId) {
		return null;
	}

	return (
		<div className={styles.wrapper}>
			{!isButtonRendered && <div aria-hidden="true" className={styles.placeholder} />}
			<div ref={containerRef} className={styles.container} />
		</div>
	);
};

export default GoogleSignInButton;
